import React, { useEffect, useRef, useState } from 'react';
import { X, Wand2, Download, RefreshCw, PenTool, Send, Copy } from 'lucide-react';
import { useUIStore } from '../stores/uiStore';
import { uploadFile } from '../services/storage';

export const ImageModal: React.FC<{ onPost?: (text: string) => void }> = ({ onPost }) => {
  const { viewingImageUrl, setViewingImageUrl } = useUIStore();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingCanvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);

  const [hue, setHue] = useState(0);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);
  const [brightness, setBrightness] = useState(100);
  const [zoom, setZoom] = useState(1);

  const [isDrawing, setIsDrawing] = useState(false);
  const [drawMode, setDrawMode] = useState(false);
  const [color, setColor] = useState('#00ffcc');
  const [brushSize, setBrushSize] = useState(5);
  const [activeFilter, setActiveFilter] = useState('none');
  const timeRef = useRef(0);
  const animationRef = useRef<number | undefined>(undefined);

  const isAnimated = (filter: string) => {
    return ['psydefx_hologram', 'psydefx_vhs', 'psydefx_neon', 'psydefx_liquid', 'kaleidoscope', 'glitch_bars', 'chroma_smash', 'xor_mult'].includes(filter);
  };

  const applyFilters = (filterType: string = activeFilter, time: number = 0) => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Clear and draw image with CSS filters applied via context
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    let filterString = `hue-rotate(${hue}deg) contrast(${contrast}%) saturate(${saturation}%) brightness(${brightness}%)`;
    if (filterType === 'invert') filterString += ' invert(100%)';
    if (filterType === 'sepia') filterString += ' sepia(100%)';
    if (filterType === 'grayscale') filterString += ' grayscale(100%)';
    
    ctx.filter = filterString;
    
    if (filterType === 'glitch') {
      // Glitch effect: draw RGB separated
      ctx.filter = filterString;
      ctx.globalCompositeOperation = 'screen';
      ctx.fillStyle = 'rgba(255, 0, 0, 1)';
      ctx.drawImage(img, 5, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgba(0, 0, 255, 1)';
      ctx.drawImage(img, -5, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgba(0, 255, 0, 1)';
      ctx.drawImage(img, 0, 5, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'source-over';
    } else if (filterType === 'kaleidoscope') {
      ctx.filter = filterString;
      for (let i = 0; i < 4; i++) {
        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((Math.PI / 2) * i + time * 0.5);
        ctx.scale(i % 2 === 0 ? 1 : -1, 1);
        ctx.globalAlpha = 0.5;
        ctx.drawImage(img, -canvas.width / 2, -canvas.height / 2, canvas.width, canvas.height);
        ctx.restore();
      }
    } else if (filterType === 'acid') {
      ctx.filter = filterString + ' saturate(300%) hue-rotate(90deg) contrast(150%) invert(20%)';
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    } else if (filterType === 'chroma_smash') {
      ctx.filter = filterString + ' saturate(500%) contrast(200%)';
      ctx.globalCompositeOperation = 'color-dodge';
      const offset = Math.sin(time * 5) * 20;
      ctx.drawImage(img, -10 + offset, -10, canvas.width + 20, canvas.height + 20);
      ctx.globalCompositeOperation = 'source-over';
    } else if (filterType === 'glitch_bars') {
      ctx.filter = filterString;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
      const yOffset = (time * 50) % 10;
      for (let y = 0; y < canvas.height; y += 10) {
        ctx.fillRect(0, y + yOffset, canvas.width, 5);
      }
    } else if (filterType === 'xor_mult') {
      ctx.filter = filterString;
      ctx.globalCompositeOperation = 'difference';
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 5 + Math.sin(time * 2) * 10, 5 + Math.cos(time * 2) * 10, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'source-over';
    } else if (filterType === 'psydefx_hologram') {
      const animatedHue = (hue + time * 50) % 360;
      ctx.filter = filterString + ` contrast(130%) saturate(150%) hue-rotate(${animatedHue}deg)`;
      ctx.globalCompositeOperation = 'screen';
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      ctx.fillStyle = `rgba(0, 255, 255, ${0.2 + Math.sin(time * 5) * 0.1})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'source-over';
    } else if (filterType === 'psydefx_vhs') {
      ctx.filter = filterString + ' contrast(150%) saturate(60%) sepia(30%) hue-rotate(-20deg)';
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const glitchX = Math.random() > 0.9 ? Math.random() * 20 - 10 : 0;
      ctx.fillStyle = 'rgba(255, 0, 0, 0.5)';
      ctx.globalCompositeOperation = 'screen';
      ctx.drawImage(img, 4 + glitchX, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgba(0, 0, 255, 0.5)';
      ctx.drawImage(img, -4 - glitchX, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
      const yOffset = (time * 100) % 8;
      for (let y = 0; y < canvas.height; y += 8) {
        ctx.fillRect(0, y + yOffset, canvas.width, 2);
      }
    } else if (filterType === 'psydefx_neon') {
      const neonHue = Math.sin(time) * 30;
      ctx.filter = filterString + ` contrast(200%) saturate(300%) hue-rotate(${neonHue}deg)`;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'color-dodge';
      ctx.globalAlpha = 0.5 + Math.sin(time * 8) * 0.2;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      ctx.globalAlpha = 1.0;
      ctx.globalCompositeOperation = 'source-over';
    } else if (filterType === 'psydefx_liquid') {
      ctx.filter = filterString + ` saturate(120%) contrast(150%) hue-rotate(${time * 20}deg)`;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'overlay';
      for (let i = 0; i < 3; i++) {
        const ox = Math.sin(i + time * 2) * 15;
        const oy = Math.cos(i + time * 2.5) * 15;
        ctx.drawImage(img, ox, oy, canvas.width, canvas.height);
      }
      ctx.globalCompositeOperation = 'source-over';
    } else {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    }
    
    ctx.filter = 'none'; // reset for drawing
  };

  useEffect(() => {
    if (!viewingImageUrl) return;

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imageRef.current = img;
      const canvas = canvasRef.current;
      const drawingCanvas = drawingCanvasRef.current;

      if (canvas && drawingCanvas) {
        // Calculate max dimensions to fit screen while maintaining aspect ratio
        const maxWidth = window.innerWidth * 0.8;
        const maxHeight = window.innerHeight * 0.8;
        let w = img.width;
        let h = img.height;

        if (w > maxWidth || h > maxHeight) {
          const ratio = Math.min(maxWidth / w, maxHeight / h);
          w = Math.floor(w * ratio);
          h = Math.floor(h * ratio);
        }

        canvas.width = w;
        canvas.height = h;
        drawingCanvas.width = w;
        drawingCanvas.height = h;

        // Clear drawing canvas (fresh start for strokes)
        const drawCtx = drawingCanvas.getContext('2d');
        if (drawCtx) drawCtx.clearRect(0, 0, w, h);

        applyFilters();
      }
    };
    img.onerror = () => {
      console.error('Failed to load image:', viewingImageUrl);
    };
    img.src = viewingImageUrl;

    // Reset state
    setHue(0);
    setContrast(100);
    setSaturation(100);
    setBrightness(100);
    setZoom(1);
    setDrawMode(false);
    setActiveFilter('none');
  }, [viewingImageUrl]);

  useEffect(() => {
    // Only apply filters statically if we aren't animating
    if (!drawMode && !isAnimated(activeFilter)) {
      applyFilters(activeFilter, 0);
    }
  }, [hue, contrast, saturation, brightness, activeFilter]);

  useEffect(() => {
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    
    if (!drawMode && isAnimated(activeFilter)) {
      let lastTime = performance.now();
      const animate = (currentTime: number) => {
        const dt = (currentTime - lastTime) / 1000;
        lastTime = currentTime;
        timeRef.current += dt;
        applyFilters(activeFilter, timeRef.current);
        animationRef.current = requestAnimationFrame(animate);
      };
      animationRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [activeFilter, hue, contrast, saturation, brightness, drawMode]);

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawMode) return;
    setIsDrawing(true);
    const canvas = drawingCanvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      lastPosRef.current = {
        x: (e.clientX - rect.left) * (canvas.width / rect.width),
        y: (e.clientY - rect.top) * (canvas.height / rect.height),
      };
    }
    draw(e);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !drawMode) return;
    const canvas = drawingCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const y = (e.clientY - rect.top) * (canvas.height / rect.height);

    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.strokeStyle = color;

    if (lastPosRef.current) {
      ctx.beginPath();
      ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
      ctx.lineTo(x, y);
      ctx.stroke();
    }

    lastPosRef.current = { x, y };
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    lastPosRef.current = null;
  };

  const handleDownload = () => {
    // Create composite canvas with both layers
    const composite = document.createElement('canvas');
    composite.width = canvasRef.current?.width ?? 0;
    composite.height = canvasRef.current?.height ?? 0;
    const compositeCtx = composite.getContext('2d');
    if (!compositeCtx) return;

    // Draw original + filters
    if (canvasRef.current) compositeCtx.drawImage(canvasRef.current, 0, 0);

    // Draw strokes on top
    if (drawingCanvasRef.current) compositeCtx.drawImage(drawingCanvasRef.current, 0, 0);

    // Download composite
    const url = composite.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = 'psydechat_edited_image.png';
    a.click();
  };

  const handlePost = async () => {
    if (!onPost) return;

    // Create composite canvas with both layers
    const composite = document.createElement('canvas');
    composite.width = canvasRef.current?.width ?? 0;
    composite.height = canvasRef.current?.height ?? 0;
    const compositeCtx = composite.getContext('2d');
    if (!compositeCtx) return;

    if (canvasRef.current) compositeCtx.drawImage(canvasRef.current, 0, 0);
    if (drawingCanvasRef.current) compositeCtx.drawImage(drawingCanvasRef.current, 0, 0);

    // Upload composite blob
    try {
      composite.toBlob(async (blob) => {
        if (!blob) return;
        const file = new File([blob], `psydefx_${Date.now()}.png`, { type: 'image/png' });
        try {
          const url = await uploadFile(file);
          if (url) {
            onPost(`![image](${url})`);
            setViewingImageUrl(null);
          }
        } catch (uploadErr) {
          console.error('Failed to upload edited image:', uploadErr);
        }
      }, 'image/png');
    } catch (err) {
      console.error('Failed to post image:', err);
    }
  };

  if (!viewingImageUrl) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 animate-in fade-in">
      <button 
        onClick={() => setViewingImageUrl(null)}
        className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-all z-50"
      >
        <X size={24} />
      </button>

      <div className="flex flex-col md:flex-row gap-6 w-full max-w-6xl max-h-full">
        {/* Main Canvas Area */}
        <div className="flex-1 flex items-center justify-center relative overflow-hidden bg-black/40 rounded-2xl border border-white/10 p-2 min-h-[50vh] overflow-auto">
          <div style={{ transform: `scale(${zoom})`, transformOrigin: 'center', transition: 'transform 0.2s ease-out' }} className="relative">
            {/* Filtered image layer */}
            <canvas
              ref={canvasRef}
              className="max-w-full max-h-full object-contain shadow-2xl"
            />

            {/* Drawing strokes overlay (positioned absolutely on top) */}
            {drawMode && (
              <canvas
                ref={drawingCanvasRef}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                className="absolute top-0 left-0 cursor-crosshair z-10"
                style={{
                  width: canvasRef.current?.width ?? 'auto',
                  height: canvasRef.current?.height ?? 'auto',
                }}
              />
            )}
          </div>
        </div>

        {/* Sidebar Controls */}
        <div className="w-full md:w-80 flex flex-col gap-4 bg-black/60 border border-white/10 rounded-2xl p-6 overflow-y-auto max-h-[80vh]">
          <h2 className="text-xl font-black text-[var(--neon)] uppercase tracking-wider mb-2 border-b border-white/10 pb-2">
            Photolab
          </h2>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-xs text-white/60 font-bold uppercase">Zoom ({(zoom * 100).toFixed(0)}%)</label>
              <input type="range" min="0.5" max="3" step="0.1" value={zoom} onChange={e => setZoom(Number(e.target.value))} className="accent-[var(--neon)]" />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-xs text-white/60 font-bold uppercase">Hue ({hue}°)</label>
              <input type="range" min="0" max="360" value={hue} onChange={e => setHue(Number(e.target.value))} className="accent-[var(--neon)]" />
            </div>
            
            <div className="flex flex-col gap-2">
              <label className="text-xs text-white/60 font-bold uppercase">Contrast ({contrast}%)</label>
              <input type="range" min="0" max="200" value={contrast} onChange={e => setContrast(Number(e.target.value))} className="accent-[var(--neon)]" />
            </div>
            
            <div className="flex flex-col gap-2">
              <label className="text-xs text-white/60 font-bold uppercase">Saturation ({saturation}%)</label>
              <input type="range" min="0" max="200" value={saturation} onChange={e => setSaturation(Number(e.target.value))} className="accent-[var(--neon)]" />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-xs text-white/60 font-bold uppercase">Brightness ({brightness}%)</label>
              <input type="range" min="0" max="200" value={brightness} onChange={e => setBrightness(Number(e.target.value))} className="accent-[var(--neon)]" />
            </div>

            <div className="grid grid-cols-2 gap-2 mt-2">
              {['none', 'invert', 'sepia', 'grayscale', 'glitch', 'kaleidoscope', 'acid', 'chroma_smash', 'glitch_bars', 'xor_mult'].map(f => (
                <button
                  key={f}
                  onClick={() => setActiveFilter(f)}
                  className={`py-1 px-1 text-[10px] font-bold uppercase rounded border ${activeFilter === f ? 'bg-[var(--neon)] text-black border-[var(--neon)]' : 'bg-white/5 text-white/60 border-white/10 hover:bg-white/10'}`}
                >
                  {f.replace('_', ' ')}
                </button>
              ))}
            </div>

            <div className="w-full h-[1px] bg-white/10 my-1" />
            <h3 className="text-xs font-black text-pastel-pink uppercase tracking-widest text-center">PsydeFX Presets</h3>

            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'psydefx_hologram', label: 'Hologram' },
                { id: 'psydefx_vhs', label: 'VHS Nightmare' },
                { id: 'psydefx_neon', label: 'Neon City' },
                { id: 'psydefx_liquid', label: 'Liquid Chrome' }
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setActiveFilter(f.id)}
                  className={`py-1 px-1 text-[10px] font-bold uppercase rounded border shadow-lg transition-all ${activeFilter === f.id ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white border-transparent' : 'bg-white/5 text-white/80 border-white/20 hover:bg-white/20 hover:border-pink-500'}`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <button 
              onClick={() => { setZoom(1); setHue(0); setContrast(100); setSaturation(100); setBrightness(100); setActiveFilter('none'); applyFilters('none'); }}
              className="flex items-center justify-center gap-2 w-full py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg text-sm transition-colors border border-white/10 mt-2"
            >
              <RefreshCw size={14} /> Reset Filters
            </button>
          </div>

          <div className="w-full h-[1px] bg-white/10 my-2" />

          <div className="flex flex-col gap-4">
            <button
              onClick={() => { setDrawMode(!drawMode); }}
              className={`flex items-center justify-center gap-2 w-full py-2 rounded-lg text-sm transition-all border ${drawMode ? 'bg-[var(--neon)] text-black border-transparent font-bold' : 'bg-white/5 text-white border-white/10 hover:bg-white/10'}`}
            >
              <PenTool size={16} /> {drawMode ? 'Drawing Mode ON' : 'Enable Drawing'}
            </button>
            
            {drawMode && (
              <div className="flex flex-col gap-3 animate-in slide-in-from-top-2 p-3 bg-white/5 rounded-lg border border-white/5">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-white/60 w-12">Color:</label>
                  <input type="color" value={color} onChange={e => setColor(e.target.value)} className="bg-transparent border-0 w-8 h-8 cursor-pointer rounded overflow-hidden" />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-white/60 w-12">Size:</label>
                  <input type="range" min="1" max="50" value={brushSize} onChange={e => setBrushSize(Number(e.target.value))} className="flex-1 accent-[var(--neon)]" />
                </div>
              </div>
            )}
          </div>

          <div className="mt-auto flex flex-col gap-2 pt-4">
            <button 
              onClick={handleDownload}
              className="flex items-center justify-center gap-2 w-full py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl text-sm font-bold transition-colors border border-white/20"
            >
              <Download size={16} /> Download
            </button>

            {onPost && (
              <button 
                onClick={handlePost}
                className="flex items-center justify-center gap-2 w-full py-3 bg-[var(--neon)] hover:bg-[var(--neon)]/80 text-black rounded-xl text-sm font-black transition-colors border border-[var(--neon)]"
              >
                <Send size={16} /> POST TO CHAT
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
